import type {
  PaigeExportPage,
  PaigeExportQuery,
  PaigeExportTiming,
  PaigeExportTimingInput,
  PaigeExportsChannel,
} from './export-types';
import { responseWords } from './problem-words';

/** One call on the job's channel (`{engineOrigin}/exports…`, `/export-timing…`); rejects with Paige's reason. */
async function call<T>(
  engineOrigin: string,
  tenant: string | undefined,
  path: string,
  method: 'GET' | 'POST' | 'PUT',
  body?: unknown,
): Promise<T> {
  const response = await fetch(`${engineOrigin}${path}`, {
    method,
    credentials: 'include',
    headers: {
      ...(body !== undefined ? { 'content-type': 'application/json' } : {}),
      ...(method === 'GET' ? {} : { 'Idempotency-Key': crypto.randomUUID() }),
      ...(tenant ? { 'Paige-Tenant': tenant } : {}),
    },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  if (!response.ok) throw new Error(await responseWords(response));
  return response.status === 204 ? (undefined as T) : ((await response.json()) as T);
}

/** The history's query string: the job's destinations only, and only the parts given. */
export function historyQuery(query: PaigeExportQuery): string {
  const search = new URLSearchParams({ destinations: 'true' });
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== '') search.set(key, String(value));
  }
  return search.toString();
}

/** The job's exports bridge over its channel (contract: listJobExports, retryJobExport, cancelJobExport, …). */
export function exportChannel(
  engineOrigin: string,
  tenant: string | undefined,
): PaigeExportsChannel {
  return {
    history: (query) =>
      call<PaigeExportPage>(engineOrigin, tenant, `/exports?${historyQuery(query)}`, 'GET'),
    retry: (id) =>
      call<void>(engineOrigin, tenant, `/exports/${encodeURIComponent(id)}/retry`, 'POST'),
    cancel: (id) =>
      call<void>(engineOrigin, tenant, `/exports/${encodeURIComponent(id)}/cancel`, 'POST'),
    timing: () => call<PaigeExportTiming>(engineOrigin, tenant, '/export-timing', 'GET'),
    setTiming: (input: PaigeExportTimingInput) =>
      call<PaigeExportTiming>(engineOrigin, tenant, '/export-timing', 'PUT', input),
    release: async () =>
      (await call<{ documents: number }>(engineOrigin, tenant, '/export-timing/release', 'POST'))
        .documents,
  };
}
